/**
 * Pure port of the composer's mode-transition decision logic — CORE-7196,
 * phase 1 of the mode/selection migration track.
 *
 * The four composer modes (CORE-5886, CONT-1285):
 *   - omni-empty:       omnibox on, 0 selected — unified editor + empty preview
 *   - omni-multi:       omnibox on, ≥2 selected — unified editor + per-channel previews
 *   - single-channel:   omnibox off, 1 selected — channel-specific editor
 *   - customized-multi: omnibox off, ≥2 selected — per-channel editors
 *
 * Today this logic is scattered across three call sites, each deciding
 * `isOmniboxEnabled` at a different moment for a different reason:
 *   - `AppInitActionCreators.loadInitialProfilesData` (init time)
 *   - `ComposerStore.ts`'s `enableDraft` (a channel/draft turning on)
 *   - `ComposerStore.ts`'s `disableDraft` (a channel/draft turning off)
 *
 * This file ports the *decision* out of each as a pure, side-effect-free
 * function — no Flux reads, no dispatches, no draft/profile mutation. It is
 * NOT wired into any live code path yet: nothing here is read by production
 * code. That's deliberate — see the module doc block below for why, and
 * `deriveModeTransition.test.ts` for the parity contract against the real
 * Flux behavior (`ComposerStore.modeTransitions.test.ts`).
 *
 * Content-copying is explicitly out of scope. The real handlers pair each
 * mode decision with a content-copy operation (`copyDraftContents` /
 * `copyDraftTextData` in `ComposerStore.ts`) that reconciles media, links,
 * retweets, and threads across per-service capability rules — that is
 * itself a whole content-migration surface already tracked separately (the
 * omni/per-channel bridge tracks in this same project), not a "mode"
 * concern. This module surfaces WHICH copy should happen and in which
 * direction as a directive (`ModeContentCopyDirective`); actually performing
 * the copy stays on the real Flux handlers for now.
 */

export type ComposerRuntimeMode = 'create' | 'edit' | 'duplicate'

/** `null` and `false` are distinct today ("never shown" vs. "shown then exited"), but both read as inactive for mode purposes. */
export type OmniboxState = boolean | null

// ---------------------------------------------------------------------------
// Init-time decision (AppInitActionCreators.loadInitialProfilesData)
// ---------------------------------------------------------------------------

export interface InitialOmniboxStateInput {
  /** Count of profiles the init flow will actually auto-select (post shouldBeAutoSelected filtering). */
  autoSelectedProfileCount: number
  /** True if the composer opened with prefill content (text/image/video/document/title/thread) — see AppInitActionCreators' hasPrefillContent. */
  hasPrefillContent: boolean
  isChannelLessDraft: boolean
  isPreviewMode: boolean
  composerMode: ComposerRuntimeMode
  /** Total connected profiles, and whether the "auto-select every connected channel" fallback would apply (profilesData.length is 2 or 3, per AppInitActionCreators' shouldAutoSelectAllChannels — the >3 and ===1 cases are covered by autoSelectedProfileCount/hasOnlyOneProfileConnected below instead, since both those paths themselves feed autoSelectedProfileCount). */
  hasOnlyOneProfileConnected: boolean
  shouldAutoSelectAllChannels: boolean
}

/**
 * Mirrors `loadInitialProfilesData`'s four `updateOmniboxState(...)` call
 * sites, in the same order (first match wins, exactly like the source's
 * sequential `if`s — none of these conditions overlap in practice, but the
 * order is preserved for faithfulness regardless).
 */
export function deriveInitialOmniboxState(
  input: InitialOmniboxStateInput,
): OmniboxState {
  const {
    autoSelectedProfileCount,
    hasPrefillContent,
    isChannelLessDraft,
    isPreviewMode,
    composerMode,
    hasOnlyOneProfileConnected,
    shouldAutoSelectAllChannels,
  } = input

  // "If multiple profiles will be auto-selected and there's already prefill
  // content, pre-enable omnibox so all channels share a single unified post
  // instead of being customized per-network."
  if (autoSelectedProfileCount > 1 && hasPrefillContent) return true

  // A channel-less ContentItem draft must open with no channels selected;
  // this unconditionally wins over anything else.
  if (isChannelLessDraft) return true

  // Otherwise, if any profile(s) get auto-selected below, the selection
  // path's own enable-time transition (deriveEnableDraftTransition) is what
  // actually determines the resulting mode — this function has nothing
  // further to say once selection is going to happen.
  const hasAutoSelectedProfiles = autoSelectedProfileCount > 0
  if (
    hasAutoSelectedProfiles ||
    hasOnlyOneProfileConnected ||
    shouldAutoSelectAllChannels
  ) {
    return null
  }

  if (isPreviewMode) return true

  // "Enable omni mode on init for new-post (create) mode so users can start
  // composing before selecting channels (content-first flow / CONT-1285).
  // Only when no profiles were auto-selected."
  if (composerMode === 'create') return true

  return null
}

// ---------------------------------------------------------------------------
// Runtime decision: a draft/channel turning on (ComposerStore.ts's enableDraft)
// ---------------------------------------------------------------------------

export type ModeContentCopyDirective =
  | { kind: 'none' }
  /** omnibox was off; a new draft is enabling alongside one already-enabled, empty draft — that lone draft's content copies nowhere, omnibox turns on instead (handled via `nextIsOmniboxEnabled`). */
  | { kind: 'noneOmniboxTakesOver' }
  /** omnibox was off; copy the last-interacted-with (or sole enabled) draft's content into the newly-enabled draft. */
  | { kind: 'fromLastInteractedDraft' }
  /** omnibox is on; copy omni's content into the just-enabled draft (fires on every enable while omni is active, including each member of a queued channel-group enable). */
  | { kind: 'fromOmniToJustEnabledDraft' }

export interface EnableDraftTransitionInput {
  isOmniboxEnabledBefore: OmniboxState
  /** Enabled-draft count *before* this draft enables, and whether that sole draft is empty — both read from the pre-enable snapshot, matching the source's ordering (it checks this before flipping `isEnabled`). Only `isSoleDraftEmpty` when count === 1. */
  enabledDraftsBefore: { count: number; isSoleDraftEmpty: boolean }
  /**
   * Synchronous selected-profile count *after* this selection. Deliberately
   * NOT derived from `enabledDraftsBefore.count + 1` — the real code reads
   * `AppStore.getSelectedProfiles().length`, not `getEnabledDrafts().length`,
   * specifically because a channel-group selection queues one COMPOSER_ENABLE
   * per member via setTimeout: on the first member's enable, enabledDrafts
   * would still read 1 even though the group's other members are already
   * selected and merely pending their own queued enable. selectedProfiles is
   * the synchronous source of truth set during the whole group's selection
   * in one pass, so it already reflects the group's full size on every
   * member's enable, not just the last one.
   */
  selectedProfileCountAfter: number
  /** `AppStore.getIsPreviewMode()` — see the preview-mode note below `isOmniboxEnabledBefore`'s live-read override in `getIsOmniboxEnabled`. */
  isPreviewMode: boolean
}

export interface EnableDraftTransitionResult {
  nextIsOmniboxEnabled: OmniboxState
  contentCopy: ModeContentCopyDirective
  /** The source's `ComposerActionCreators.expand(id)` call, fired only when omni settles to exactly one selected profile. */
  shouldExpandJustEnabledDraft: boolean
}

export function deriveEnableDraftTransition(
  input: EnableDraftTransitionInput,
): EnableDraftTransitionResult {
  const {
    isOmniboxEnabledBefore,
    enabledDraftsBefore,
    selectedProfileCountAfter,
    isPreviewMode,
  } = input

  // In preview mode, `AppStore.getIsOmniboxEnabled()` ignores the stored flag
  // entirely and derives live from the selected count instead
  // (`selectedProfiles.length === 0`) — every `updateOmniboxState` call this
  // function models below is invisible to it. Nothing in the real
  // `enableDraft` changes the selected count between its own "before" and
  // "after" reads, so one live value covers both here too.
  const isOmniboxEnabledLive = isPreviewMode
    ? selectedProfileCountAfter === 0
    : isOmniboxEnabledBefore

  let nextIsOmniboxEnabled = isOmniboxEnabledLive
  let contentCopy: ModeContentCopyDirective = { kind: 'none' }
  let shouldExpandJustEnabledDraft = false

  // Omnibox currently off (`false` or `null` both count as "off" here, per
  // `!AppStore.getIsOmniboxEnabled()`).
  if (!isOmniboxEnabledLive) {
    const hasOneEmptyEnabledDraft =
      enabledDraftsBefore.count === 1 && enabledDraftsBefore.isSoleDraftEmpty

    if (hasOneEmptyEnabledDraft) {
      nextIsOmniboxEnabled = true
      contentCopy = { kind: 'noneOmniboxTakesOver' }
    } else if (enabledDraftsBefore.count >= 1) {
      contentCopy = { kind: 'fromLastInteractedDraft' }
    }
    // enabledDraftsBefore.count === 0: neither branch fires, matching the
    // source exactly — the very first draft to ever enable has nothing to
    // copy from and nothing to auto-enable omnibox for.
  }

  // Omnibox on (either already on before this call, or just turned on above
  // by the `noneOmniboxTakesOver` branch — the source re-reads
  // `AppStore.getIsOmniboxEnabled()` fresh after that write, so this branch
  // must run in the SAME call when both apply).
  if (nextIsOmniboxEnabled) {
    contentCopy =
      contentCopy.kind === 'none'
        ? { kind: 'fromOmniToJustEnabledDraft' }
        : contentCopy

    // Auto-exit omni whenever the user settles on exactly one channel.
    if (selectedProfileCountAfter === 1) {
      nextIsOmniboxEnabled = null
      shouldExpandJustEnabledDraft = true
    }
  }

  // The live getter overrides the stored flag entirely in preview mode, so
  // that's always its final answer — regardless of what the stored-flag
  // dance above computed (every `updateOmniboxState` call is a no-op for it),
  // and always a strict boolean, never the `null` "exited" state modeled
  // above (preview mode has no "customize for each network" to exit into).
  if (isPreviewMode) {
    nextIsOmniboxEnabled = isOmniboxEnabledLive
    shouldExpandJustEnabledDraft = false
  }

  return { nextIsOmniboxEnabled, contentCopy, shouldExpandJustEnabledDraft }
}

// ---------------------------------------------------------------------------
// Runtime decision: a draft/channel turning off (ComposerStore.ts's disableDraft)
// ---------------------------------------------------------------------------

export interface DisableDraftTransitionInput {
  isOmniboxEnabledBefore: OmniboxState
  /** Enabled-draft count *after* this draft disables — the source reads `getEnabledDrafts()` post-disable here, unlike enableDraft's pre-enable read above. */
  enabledDraftsAfter: { count: number }
  composerMode: ComposerRuntimeMode
}

export interface DisableDraftTransitionResult {
  nextIsOmniboxEnabled: OmniboxState
  contentCopy: ModeContentCopyDirective
}

export function deriveDisableDraftTransition(
  input: DisableDraftTransitionInput,
): DisableDraftTransitionResult {
  const { isOmniboxEnabledBefore, enabledDraftsAfter, composerMode } = input

  // These two conditions (=== 1 and === 0 remaining enabled drafts) are
  // mutually exclusive by construction, matching the source's independent
  // (non-else-chained) `if`s.
  if (isOmniboxEnabledBefore && enabledDraftsAfter.count === 1) {
    return {
      nextIsOmniboxEnabled: null,
      contentCopy: { kind: 'fromOmniToJustEnabledDraft' }, // copies omni -> the one remaining draft
    }
  }

  if (enabledDraftsAfter.count === 0) {
    // create and duplicate treat 0 channels as a valid channel-less draft;
    // editing a real post does not, so it keeps the "no channels" state
    // (never re-enters omni-empty).
    if (composerMode === 'create' || composerMode === 'duplicate') {
      return {
        nextIsOmniboxEnabled: true,
        contentCopy: { kind: 'fromLastInteractedDraft' }, // copies the last-interacted (or just-disabled) draft's content into omni
      }
    }
  }

  return {
    nextIsOmniboxEnabled: isOmniboxEnabledBefore,
    contentCopy: { kind: 'none' },
  }
}

// ---------------------------------------------------------------------------
// Shared: classify (isOmniboxEnabled, selectedCount) into one of the four modes.
// ---------------------------------------------------------------------------

export type ComposerMode =
  | 'omni-empty'
  | 'omni-multi'
  | 'single-channel'
  | 'customized-multi'
  | 'unexpected'

export function classifyMode(
  isOmniboxEnabled: OmniboxState,
  selectedProfileCount: number,
): ComposerMode {
  if (isOmniboxEnabled === true && selectedProfileCount === 0)
    return 'omni-empty'
  if (isOmniboxEnabled === true && selectedProfileCount >= 2)
    return 'omni-multi'
  if (!isOmniboxEnabled && selectedProfileCount === 1) return 'single-channel'
  if (!isOmniboxEnabled && selectedProfileCount >= 2) return 'customized-multi'
  return 'unexpected'
}
